import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  CookieType,
  useCookieContext,
} from 'components/styled/cookie-consent/context'
import config from 'config'
import { throttle } from 'lodash'
import { getCompanyDataForIntercom } from 'selectors'

const APP_ID = config.INTERCOM_APP_ID
const COOKIE_DOMAIN = config.INTERCOM_COOKIE_DOMAIN
const INTERCOM_API_BASE_EU = 'https://api-iam.eu.intercom.io'
const ONE_HOUR_IN_MS = 60 * 60 * 1000
const INTERCOM_SESSION_DURATION = 48 * ONE_HOUR_IN_MS // 48 hours

let shouldAddUserDataLater = false

const propTypes = {
  language: PropTypes.string,
  showIntercom: PropTypes.bool,
  currentUser: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  actionBarHeight: PropTypes.number,
  pathname: PropTypes.string,
}

const defaultProps = {
  language: 'en',
  showIntercom: false,
  currentUser: undefined,
  actionBarHeight: 0,
  pathname: '',
}

const getOverrideLanguage = langKey => {
  switch (langKey) {
    case 'se':
      return 'sv'
    case 'fi':
      return 'fi'
    default:
      return 'en'
  }
}

const baseData = language => ({
  app_id: APP_ID,
  api_base: INTERCOM_API_BASE_EU,
  language_override: getOverrideLanguage(language),
  session_duration: INTERCOM_SESSION_DURATION,
  hide_default_launcher: false,
  alignment: 'right',
})

export const startIntercomTour = tourId => {
  if (window.Intercom) {
    window.Intercom('startTour', tourId)
  }
}

export const clearIntercomCookies = clearAll => {
  const allCookies = document.cookie.split(';')
  for (let i = 0; i < allCookies.length; i += 1) {
    const cookie = allCookies[i].trim()
    const cookieName = cookie.split('=')[0]

    if (
      (clearAll && cookieName.indexOf('intercom-') === 0) ||
      cookieName.indexOf('intercom-session') === 0
    ) {
      document.cookie = `${cookieName}=;domain=${COOKIE_DOMAIN};expires=Thu, 01 Jan 1970 00:00:00 GMT;max-age=0;path=/`
    }
  }
}

const hideIntercomWidget = () => {
  if (window.Intercom) {
    window.Intercom('update', {
      hide_default_launcher: true,
    })
  }
}

const showIntercomWidget = () => {
  if (window.Intercom) {
    window.Intercom('update', {
      hide_default_launcher: false,
    })
  }
}

// remove later
export const disableIntercom = () => {
  hideIntercomWidget()
}

export async function clearUserDataFromIntercom(language) {
  if (window.Intercom) {
    // reset session data from itercom and boot with base data
    await window.Intercom('shutdown')
    window.Intercom('boot', baseData(language))
  }
}

export const addUserDataToIntercom = currentUser => {
  if (window.Intercom && currentUser) {
    // update session user data to intercom
    const { sessionUserPermissions, companyData, salesforceId } =
      getCompanyDataForIntercom(currentUser)

    const permissionsData = sessionUserPermissions
      ? {
          permission_see_all_positions: sessionUserPermissions.seeAllPositions,
          permission_create_job_positions:
            sessionUserPermissions.createJobPositions,
          permission_create_video_interviews:
            sessionUserPermissions.createInterviews,
          permission_edit_company_info: sessionUserPermissions.account,
          permission_billing: sessionUserPermissions.billing,
          permission_reporting: sessionUserPermissions.reporting,
          permission_edit_users: sessionUserPermissions.editUsers,
          has_default_permissions: sessionUserPermissions.default || false,
        }
      : {}

    const currentUserData = {
      user_id: currentUser._id,
      name: currentUser.name,
      email: currentUser.email,
      deactivated: currentUser.deactivated,
      newsletter: currentUser.newsletter,
      created_at: currentUser.createdAt,
      before_billing_epoch: !currentUser.isAfterBillingEpoch,
      language_override: getOverrideLanguage(currentUser.language),
      company: companyData,
      salesforce_id: salesforceId,
      ...permissionsData,
    }

    window.Intercom('update', currentUserData)
  } else {
    shouldAddUserDataLater = true
  }
}

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = event => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, handler])
}

export const Intercom = ({
  language,
  showIntercom,
  currentUser,
  actionBarHeight,
  pathname,
}) => {
  const [currentHorizontalPadding, setCurrentHorizontalPadding] =
    useState(undefined)
  const [currentVerticalPadding, setCurrentVerticalPadding] =
    useState(undefined)

  const usePrevious = value => {
    const ref = useRef()
    useEffect(() => {
      ref.current = value
    })
    return ref.current
  }

  const prevHorizontalPadding = usePrevious(currentHorizontalPadding)
  const prevVerticalPadding = usePrevious(currentVerticalPadding)

  const updateDimensions = throttle(() => {
    if (window.Intercom) {
      if (document.body.clientWidth >= 1600 || actionBarHeight === 0) {
        const newValues = { h: 16, v: 20 }
        if (
          newValues.h !== currentHorizontalPadding ||
          newValues.v !== currentVerticalPadding
        ) {
          setCurrentHorizontalPadding(newValues.h)
          setCurrentVerticalPadding(newValues.v)
        }
      } else if (document.body.clientWidth >= 1500) {
        const newValues = { h: 48, v: actionBarHeight + 16 }
        if (
          newValues.h !== currentHorizontalPadding ||
          newValues.v !== currentVerticalPadding
        ) {
          setCurrentHorizontalPadding(newValues.h)
          setCurrentVerticalPadding(newValues.v)
        }
      } else {
        const newValues = { h: 16, v: actionBarHeight + 16 }
        if (
          newValues.h !== currentHorizontalPadding ||
          newValues.v !== currentVerticalPadding
        ) {
          setCurrentHorizontalPadding(newValues.h)
          setCurrentVerticalPadding(newValues.v)
        }
      }
    }
  }, 1000)

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('update')
    }
  }, [pathname])

  useEffect(() => {
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    const shouldUpdate =
      prevHorizontalPadding !== currentHorizontalPadding ||
      prevVerticalPadding !== currentVerticalPadding

    if (window.Intercom && shouldUpdate) {
      window.Intercom('update', {
        horizontal_padding: currentHorizontalPadding,
        vertical_padding: currentVerticalPadding,
      })
    }
    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
  }, [actionBarHeight, currentHorizontalPadding, currentVerticalPadding])

  const ref = useRef()

  const { allowedCookies } = useCookieContext()
  const isFunctionalAllowed = allowedCookies.includes(CookieType.Functional)

  useOnClickOutside(ref, () => window.Intercom && window.Intercom('hide'))

  const init = () => {
    window.intercomSettings = baseData(language)
    /*eslint-disable*/
    ;(function () {
      var w = window
      var ic = w.Intercom
      if (typeof ic === 'function') {
        ic('reattach_activator')
        ic('update', w.intercomSettings)
      } else {
        var d = document
        var i = function () {
          i.c(arguments)
        }
        i.q = []
        i.c = function (args) {
          i.q.push(args)
        }
        w.Intercom = i
        var l = function () {
          if (!showIntercom) {
            hideIntercomWidget()
          } else {
            var s = d.createElement('script')
            s.type = 'text/javascript'
            s.async = true
            s.src = 'https://widget.intercom.io/widget/' + APP_ID
            var x = d.getElementsByTagName('script')[0]
            x.parentNode.insertBefore(s, x)
          }
        }
        if (document.readyState === 'complete') {
          l()
        } else if (w.attachEvent) {
          w.attachEvent('onload', l)
        } else {
          w.addEventListener('load', l, false)
        }
      }
    })()
  }

  const handleIntercomInit = () => {
    if (isFunctionalAllowed && showIntercom && !window.Intercom) {
      init()
      showIntercomWidget() // if window.Intercom exists will make sure widget is not hidden
    }
  }

  useEffect(() => {
    const hasUserIdAndToken = !!(
      currentUser &&
      currentUser._id &&
      currentUser.csrfToken
    )
    const intercomState = localStorage.getItem(
      `intercom.intercom-state-${APP_ID}`
    )
    const parsedIntercomState = intercomState
      ? JSON.parse(intercomState)
      : undefined
    const intercomStateRoleIsUser =
      parsedIntercomState &&
      parsedIntercomState.user &&
      parsedIntercomState.user.role === 'user'
    if (intercomStateRoleIsUser && !hasUserIdAndToken) {
      // if user role is 'user', clear the cookies. User conversations are not lost
      // when clearing cookies as user data is updated when session is fetched and user conversations are fetched too
      // so user session is always cleared but on user-auth pages the user data gets updated there again.
      Promise.resolve(clearIntercomCookies(true)).then(() => {
        handleIntercomInit()
      })
    } else {
      // if user role is 'visitor' just call init
      handleIntercomInit()
    }

    if (hasUserIdAndToken && shouldAddUserDataLater) {
      addUserDataToIntercom(currentUser)
    }

    if (!showIntercom) {
      hideIntercomWidget()
    }
  }, [
    isFunctionalAllowed,
    language,
    showIntercom,
    currentUser._id,
    shouldAddUserDataLater,
  ])

  return <div ref={ref} />
}

Intercom.propTypes = propTypes
Intercom.defaultProps = defaultProps
